/**
 * The hands-free control for the preference interview.
 *
 * Everything here is designed for a glance, not a read. The person using it may
 * be driving, which sets the whole brief: one control, one line of state, text
 * large enough to resolve in peripheral vision, and no action that requires
 * aiming at a small target. The transcript, the option cards, and the take
 * panel all keep working underneath — this doesn't replace the page, it's a
 * second way into the same conversation.
 *
 * The stop control is deliberately the only other button. "Cancel", "retry",
 * "skip" and friends all belong to a stationary user; in a car every extra
 * target is a reason to look down.
 */
import { Loader2, Mic, MicOff, Square, Volume2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { VoicePhase, VoiceSession } from "@/lib/voice/useVoiceSession";
import { cn } from "@/lib/utils";

/** One line of state, phrased as what's happening rather than what mode we're in. */
const PHASE_LABEL: Record<VoicePhase, string> = {
  idle: "Voice is off",
  starting: "Starting…",
  listening: "Listening — just talk",
  thinking: "Thinking…",
  speaking: "Speaking",
  error: "Voice stopped",
};

function PhaseIcon({ phase }: { phase: VoicePhase }) {
  const className = "h-5 w-5 shrink-0";
  switch (phase) {
    case "listening":
      // The one state with motion: from the corner of an eye, "is it hearing
      // me?" is the only question that matters, and a static mic can't answer it.
      return <Mic className={cn(className, "animate-pulse text-bw-blue")} aria-hidden />;
    case "speaking":
      return <Volume2 className={cn(className, "text-bw-blue")} aria-hidden />;
    case "starting":
    case "thinking":
      return <Loader2 className={cn(className, "animate-spin text-bw-ink-muted")} aria-hidden />;
    default:
      return <MicOff className={cn(className, "text-bw-ink-muted")} aria-hidden />;
  }
}

export function VoiceInterview({
  session,
  disabled,
  disabledReason,
}: {
  session: VoiceSession;
  /** True while the topic is locked (finished, or a typed turn is in flight). */
  disabled?: boolean;
  disabledReason?: string;
}) {
  const { phase, partial, lastReply, error, start, stop, barge } = session;
  const running = phase !== "idle" && phase !== "error";

  return (
    <div
      className={cn(
        "flex flex-col gap-3 rounded-card border p-4 transition-colors",
        running ? "border-bw-blue bg-bw-blue/5" : "border-bw-blue-muted/40",
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        <PhaseIcon phase={phase} />
        <div className="flex min-w-0 flex-1 flex-col">
          {/* role=status so a screen reader announces phase changes without
              stealing focus — the same information the icon carries. */}
          <span className="font-sans text-sm font-semibold text-bw-navy" role="status">
            {PHASE_LABEL[phase]}
          </span>
          {!running && !error && (
            <span className="font-sans text-xs text-bw-ink-muted">
              Answer out loud instead of typing.
            </span>
          )}
          {phase === "speaking" && (
            <span className="font-sans text-xs text-bw-ink-muted">
              Press pause on your steering wheel or headset to jump in.
            </span>
          )}
        </div>

        {running ? (
          <div className="flex items-center gap-2">
            {phase === "speaking" && (
              <Button variant="outline" size="lg" onClick={barge} className="min-h-12">
                My turn
              </Button>
            )}
            <Button
              variant="outline"
              size="lg"
              onClick={stop}
              className="min-h-12"
              aria-label="Stop voice"
            >
              <Square className="mr-2 h-4 w-4" aria-hidden /> Stop
            </Button>
          </div>
        ) : (
          <Button
            size="lg"
            onClick={start}
            disabled={disabled}
            // Tall enough to hit without aiming. The default button height is a
            // desk-and-mouse size and is not usable from a driver's seat.
            className="min-h-12 px-6"
          >
            <Mic className="mr-2 h-4 w-4" aria-hidden />
            {phase === "error" ? "Try voice again" : "Answer by voice"}
          </Button>
        )}
      </div>

      {disabled && disabledReason && !running && (
        <p className="font-sans text-xs text-bw-ink-muted">{disabledReason}</p>
      )}

      {error && (
        <p className="font-sans text-sm text-bw-navy" role="alert">
          {error}
        </p>
      )}

      {/* Live captions. Text is oversized on purpose: it has to be readable at a
          glance, and it doubles as the fallback when the car's audio is too
          quiet or the passenger wants to follow along. */}
      {(partial || (running && lastReply)) && (
        <div className="flex flex-col gap-2 border-t border-bw-blue-muted/30 pt-3">
          {running && lastReply && phase !== "listening" && (
            <p className="font-sans text-base leading-snug text-bw-ink">{lastReply}</p>
          )}
          {partial && (
            <p className="font-sans text-base italic leading-snug text-bw-ink-muted">
              “{partial}”
            </p>
          )}
        </div>
      )}
    </div>
  );
}
